/**
 * L'indice della guida: quali pagine esistono, in che ordine, e **quale
 * schermata spiega ciascuna**.
 *
 * Il testo non è qui: sta in `docs/guida/`, un file Markdown per pagina,
 * versionato come il resto. Il vantaggio è quello scritto in
 * `docs/spec/guida.md` — fra due versioni si vede **cosa è cambiato**, come per
 * il codice — e il costo è che qui va tenuto un elenco. È un costo piccolo e
 * pagato una volta: quindici righe.
 *
 * *Fino al 13 settembre 2026 tutto quello che qui si chiama «Guida» era un «?»
 * tondo nell'intestazione. È stato tolto quel giorno — era superfluo accanto
 * alla voce «Guida», e nel frattempo il «?» era diventato il segno che apre una
 * spiegazione, quindi due «?» facevano due cose diverse. La voce «Guida» ha
 * ereditato il suo mestiere: porta alla pagina della schermata in cui si è.*
 *
 * **Questo file non tocca il disco**, ed è deliberato: lo importa anche la voce
 * «Guida» della striscia delle sezioni, che è codice cliente. Chi legge i file è
 * `guida-testo.ts`, che sta solo sul server.
 */

/** Le due famiglie di pagine, che sull'indice diventano due titoli. */
export type GruppoGuida = "uso" | "genitori";

export type VoceGuida = {
  /** Il nome del file in `docs/guida/`, senza `.md`, e l'indirizzo della pagina. */
  slug: string;
  /**
   * Il titolo, che è anche la riga `# ...` in cima al file.
   *
   * Sì, è scritto due volte. La ragione è che l'indice deve poter elencare
   * tredici titoli senza aprire tredici file, e la voce «Guida» gira nel
   * browser dove i file non esistono. Perché le due copie non divergano c'è un
   * test che le confronta — è il genere di divergenza che nessuno noterebbe
   * mai guardando.
   */
  titolo: string;
  gruppo: GruppoGuida;
  /**
   * Gli indirizzi dell'applicazione che questa pagina spiega.
   *
   * È da qui che «Guida» sa dove portare: si guarda l'indirizzo aperto e si
   * cerca la voce che lo dichiara. Una pagina può spiegarne più d'uno, e
   * qualcuna non ne ha nessuno — «Il riquadro rosso» compare ovunque e non ha
   * un indirizzo suo.
   */
  percorsi: readonly string[];
};

/**
 * L'ordine è quello in cui si incontrano usando StudyHub, non quello
 * alfabetico: si entra, si sbaglia la password, si attiva il codice, si guarda
 * la striscia in alto, si caricano le pagine.
 */
export const PAGINE_GUIDA: readonly VoceGuida[] = [
  { slug: "entrare", titolo: "Entrare", gruppo: "uso", percorsi: ["/accesso"] },
  {
    slug: "codice-a-sei-cifre",
    titolo: "Il codice a sei cifre",
    gruppo: "uso",
    percorsi: ["/accesso/codice"],
  },
  {
    slug: "attivare-il-secondo-fattore",
    titolo: "Attivare il secondo fattore",
    gruppo: "uso",
    percorsi: ["/il-mio-account/secondo-fattore"],
  },
  {
    slug: "ho-dimenticato-la-password",
    titolo: "Ho dimenticato la password",
    gruppo: "uso",
    percorsi: ["/password-dimenticata"],
  },
  {
    slug: "password-nuova",
    titolo: "Scegliere una password nuova",
    gruppo: "uso",
    percorsi: ["/ripristino"],
  },
  /*
   * **La pagina iniziale ha una pagina sua dalla 9a**, perché da quel giorno ha
   * un mestiere — «Da fare», le voci dei prossimi sette giorni. Fino a lì «/»
   * portava a «La striscia blu in alto», che la pagina iniziale la nominava
   * soltanto; adesso quella pagina non dichiara nessuna schermata, come «Il
   * riquadro rosso»: spiega una cosa che sta su tutte.
   */
  {
    slug: "pagina-iniziale",
    titolo: "La pagina iniziale",
    gruppo: "uso",
    percorsi: ["/"],
  },
  {
    slug: "la-striscia-blu",
    titolo: "La striscia blu in alto",
    gruppo: "uso",
    percorsi: [],
  },
  {
    slug: "il-riquadro-rosso",
    titolo: "Il riquadro rosso in cima alla pagina",
    gruppo: "uso",
    percorsi: [],
  },
  /*
   * «Cercare» dalla milestone 8, prima delle altre pagine d'uso dopo la striscia:
   * «Cerca» è la prima voce della striscia delle sezioni. Copre anche
   * `/cerca/apri`, che però non si vede mai: rimbalza sulla pagina aperta.
   */
  {
    slug: "cercare",
    titolo: "Cercare",
    gruppo: "uso",
    percorsi: ["/cerca"],
  },
  {
    slug: "caricare-le-pagine",
    titolo: "Caricare le pagine",
    gruppo: "uso",
    percorsi: ["/da-assegnare"],
  },
  {
    slug: "ritoccare-una-foto",
    titolo: "Ritoccare una foto venuta male",
    gruppo: "uso",
    /*
     * **Il primo percorso con un pezzo variabile in mezzo**, e per questo
     * `copre` sa leggere il `*`. L'identificativo di una fotografia sta fra due
     * pezzi fissi, quindi un prefisso non basta: senza il `*`, «Guida» da questa
     * schermata finirebbe su «Caricare le pagine», che la nomina e non la
     * spiega.
     */
    percorsi: ["/da-assegnare/*/ritocca"],
  },
  {
    slug: "fotografare-col-telefono",
    titolo: "Fotografare dal telefono col QR",
    gruppo: "uso",
    percorsi: ["/fotografa"],
  },
  {
    slug: "il-mio-account",
    titolo: "Il mio account",
    gruppo: "uso",
    percorsi: ["/il-mio-account"],
  },
  /*
   * «Libri e quaderni» e «Scuola» dalla milestone 7a, fra le pagine d'uso: le
   * aprono tutti e tre, studente compreso. Prima «Libri e quaderni», che è
   * l'ordine della striscia in alto.
   */
  {
    slug: "libri-e-quaderni",
    titolo: "Libri e quaderni",
    gruppo: "uso",
    // Copre anche la scheda di un singolo contenitore: è la stessa funzione
    // vista da vicino.
    percorsi: ["/libri-e-quaderni"],
  },
  /*
   * **Il confronto fra due scatti** — milestone 7f, 20 settembre 2026. Ha una
   * pagina sua e non una sezione dentro «Caricare le pagine», perché la stessa
   * schermata vive in **due posti** — fra le foto da assegnare e dentro un
   * volume — e perché l'unione di due scatti è una cosa che si spiega una volta
   * e si fa di rado: è esattamente ciò che una pagina di guida serve a tenere.
   */
  {
    slug: "confrontare-due-scatti",
    titolo: "Confrontare due scatti, e unirli",
    gruppo: "uso",
    percorsi: ["/da-assegnare/confronta", "/libri-e-quaderni/*/confronta"],
  },
  /*
   * **Il calendario del diario** — milestone 7f. Una schermata sua, figlia
   * della scheda del diario, e una pagina di guida sua: è l'unico posto del
   * sito in cui si cerca per **giorno** invece che per volume.
   */
  {
    slug: "il-calendario-del-diario",
    titolo: "Il calendario del diario",
    gruppo: "uso",
    percorsi: ["/libri-e-quaderni/*/calendario"],
  },
  /*
   * «Compiti» dalla milestone 9a, fra «Libri e quaderni» e «Scuola» come nella
   * striscia. Copre anche la pagina di una voce, che è la stessa funzione vista
   * da vicino; il calendario ha una pagina sua, come quello del diario.
   */
  { slug: "compiti", titolo: "Compiti", gruppo: "uso", percorsi: ["/compiti"] },
  {
    slug: "il-calendario-dei-compiti",
    titolo: "Il calendario dei compiti",
    gruppo: "uso",
    percorsi: ["/compiti/calendario"],
  },
  /*
   * **«Lo zaino»** dalla milestone 9c, subito dopo «Compiti»: è la risposta a
   * «cosa porto domani», e il riquadro che la mostra sta sulla pagina iniziale,
   * accanto alle voci. Non ha una voce nella striscia in alto — ci si arriva dal
   * riquadro e dall'orario — e proprio per questo una pagina di guida sua serve
   * più che alle altre.
   */
  { slug: "lo-zaino", titolo: "Lo zaino", gruppo: "uso", percorsi: ["/zaino"] },
  /*
   * **«Le mail di ogni giorno»** dalla milestone 9d, subito dopo «Lo zaino»:
   * due delle tre mail sono lo zaino che arriva da sé, e la terza sono i
   * compiti, quindi sta fra le due pagine di cui parla.
   *
   * **Non ha nessun percorso**, e non è una dimenticanza: è l'unica pagina di
   * guida che descrive una cosa che **non sta su nessuna schermata**. Le mail
   * arrivano nella casella di posta; sul sito ci sono soltanto i pulsanti per
   * rimandarle, e quelli vivono dentro «Lo zaino», «Compiti» e «Stato», che
   * hanno già la loro pagina. Un percorso qui avrebbe rubato «Guida» a una di
   * quelle tre.
   */
  { slug: "le-mail-di-ogni-giorno", titolo: "Le mail di ogni giorno", gruppo: "uso", percorsi: [] },
  /*
   * **«Riassunti»** dalla milestone 10-1, **prima di «Scuola»**: è l'ordine
   * della striscia in alto, dove sta subito dopo «Libri e quaderni» — si
   * sfoglia, si spunta, si chiede.
   *
   * Il percorso dichiarato è uno solo e copre tutto quello che sta sotto: la
   * schermata che chiede un riassunto e quella che lo mostra sono la stessa
   * funzione vista da vicino, come per «Da assegnare».
   */
  { slug: "riassunti", titolo: "Riassunti", gruppo: "uso", percorsi: ["/riassunti"] },
  /*
   * **«Rispiegamelo»** (10 ottobre 2026), subito dopo «Riassunti», come nella
   * striscia in alto: l'elenco, la richiesta e la spiegazione sono la stessa
   * funzione vista da vicino.
   */
  { slug: "rispiegamelo", titolo: "Rispiegamelo", gruppo: "uso", percorsi: ["/rispiegamelo"] },
  /*
   * **«Mappe»** (12 ottobre 2026), subito dopo «Rispiegamelo», come nella
   * striscia in alto: l'elenco, la richiesta, la mappa e le sue spiegazioni sono
   * la stessa funzione vista da vicino.
   */
  { slug: "mappe", titolo: "Mappe", gruppo: "uso", percorsi: ["/mappe"] },
  /*
   * **«Prove»** (10-2): l'elenco, «Nuova prova» e la pagina di una prova sono la
   * stessa funzione vista da vicino, come per i riassunti.
   */
  { slug: "prove", titolo: "Prove", gruppo: "uso", percorsi: ["/prove"] },
  /*
   * **«Esercizi»** (11-1): la pagina di un esercizio, con i suoi scalini. Dal
   * 3 ottobre 2026 anche l'elenco, coi filtri e il PDF delle selezionate: la
   * stessa funzione vista da lontano, come per i riassunti.
   */
  { slug: "esercizi", titolo: "Esercizi", gruppo: "uso", percorsi: ["/esercizi"] },
  /*
   * **«Verifiche»** (10a, 19 ottobre 2026), subito dopo «Esercizi», come nella
   * striscia in alto: una verifica è un gruppo di esercizi, e l'elenco, la scelta
   * e la pagina di una verifica sono la stessa funzione.
   */
  { slug: "verifiche", titolo: "Verifiche", gruppo: "uso", percorsi: ["/verifiche"] },
  /*
   * **«Correggi compiti»** (11-3): la sezione e la pagina di un foglio, la
   * stessa funzione vista da lontano e da vicino.
   */
  { slug: "correggi-compiti", titolo: "Correggi compiti", gruppo: "uso", percorsi: ["/correggi-compiti"] },
  /*
   * **«Letture in corso»** (6f): la pagina delle letture a metà prezzo. Ci si
   * arriva da un volume e da Stato, e vive a `/letture`.
   */
  { slug: "letture-in-corso", titolo: "Letture in corso", gruppo: "uso", percorsi: ["/letture"] },
  { slug: "scuola", titolo: "Scuola", gruppo: "uso", percorsi: ["/scuola"] },
  /*
   * **«L'orario»** è figlio di «Scuola», come il calendario lo è del diario, e
   * va **dopo** di lei: vince il percorso più lungo, quindi da `/scuola/orario`
   * «Guida» arriva qui e non sulla pagina della madre.
   */
  { slug: "orario", titolo: "L'orario", gruppo: "uso", percorsi: ["/scuola/orario"] },
  /*
   * **«I ricevimenti»** (milestone «ricevimenti», secondo tempo): figlia di
   * «Scuola» come l'orario, e per la stessa ragione dopo di lei.
   */
  { slug: "ricevimenti", titolo: "I ricevimenti", gruppo: "uso", percorsi: ["/scuola/ricevimenti"] },
  /*
   * **«Spesa» sta fra le pagine d'uso e non fra quelle dei genitori**, ed è la
   * stessa ragione per cui quella voce, nella striscia in alto, è l'unica che
   * non distingua i ruoli: la pagina la apre chiunque, studente compreso
   * (`docs/spec/costi.md`, 5 settembre 2026 — *tutti vedono gli stessi
   * numeri*). Quello che è riservato ai genitori è **cambiare** il tetto, non
   * guardare la spesa.
   */
  {
    slug: "spesa",
    titolo: "Spesa",
    gruppo: "uso",
    percorsi: ["/spesa"],
  },
  /*
   * **«Uso dell'AI»** (11-5, secondo tempo) dopo «Spesa», come nella striscia
   * in alto, e fra le pagine d'uso per la stessa ragione: la apre anche lo
   * studente.
   */
  { slug: "uso-dell-ai", titolo: "Uso dell'AI", gruppo: "uso", percorsi: ["/uso-dell-ai"] },
  {
    slug: "cambiare-spazio",
    titolo: "Cambiare spazio",
    gruppo: "genitori",
    percorsi: [],
  },
  // «Stato» prima di «Utenti» perché è l'ordine della striscia in alto, che è
  // l'unico posto da cui ci si arriva: chi cerca la pagina di una schermata la
  // cerca dove quella schermata sta.
  { slug: "stato", titolo: "Stato", gruppo: "genitori", percorsi: ["/stato"] },
  { slug: "utenti", titolo: "Utenti", gruppo: "genitori", percorsi: ["/utenti"] },
  // Dopo «Utenti», cioè nello stesso ordine della striscia in alto: chi cerca
  // la pagina di una schermata la cerca dove quella schermata sta.
  {
    slug: "impostazioni",
    titolo: "Impostazioni",
    gruppo: "genitori",
    percorsi: ["/impostazioni"],
  },
  /*
   * **«Le notifiche sul telefono»** (11-4, secondo tempo), dopo «Impostazioni»
   * da cui si attivano. **Nessun percorso**, come «Le mail di ogni giorno»: la
   * notifica arriva sul telefono e non sta su nessuna schermata, e i pulsanti
   * vivono dentro Impostazioni e Stato, che hanno già la loro pagina.
   */
  { slug: "notifiche-sul-telefono", titolo: "Le notifiche sul telefono", gruppo: "genitori", percorsi: [] },
  /*
   * **«Internet di Mirko»** (primo tempo, 31 ottobre 2026), dopo
   * «Impostazioni» come nella striscia in alto.
   */
  { slug: "internet", titolo: "Internet di Mirko", gruppo: "genitori", percorsi: ["/internet"] },
];

/** I due titoli dell'indice. */
export const NOMI_GRUPPI: Readonly<Record<GruppoGuida, string>> = {
  uso: "Usare StudyHub",
  genitori: "Per mamma e papà",
};

/** L'indirizzo dell'indice, scritto una volta sola. */
export const PERCORSO_GUIDA = "/guida";

/** La pagina con quel nome, oppure `null` se quel nome non esiste. */
export function voceGuida(slug: string): VoceGuida | null {
  return PAGINE_GUIDA.find((voce) => voce.slug === slug) ?? null;
}

/**
 * Vero se quell'indirizzo cade dentro quel percorso dichiarato.
 *
 * `/da-assegnare` copre anche `/da-assegnare/<foto>`: la pagina di una singola
 * fotografia è la stessa funzione vista da vicino, e mandare «Guida» all'indice
 * proprio lì sarebbe il posto peggiore per abbandonare qualcuno.
 *
 * `/` è l'eccezione, e va trattata a parte: come prefisso coprirebbe **tutto**,
 * cioè vincerebbe sempre e nessun'altra voce servirebbe più a niente.
 */
function copre(dichiarato: string, percorso: string): boolean {
  if (dichiarato === "/") {
    return percorso === "/";
  }

  /*
   * **Una stellina sta per un pezzo qualunque, e uno solo.** Serve alla
   * schermata del ritocco, che dalla milestone 6c è la prima il cui indirizzo ha
   * una parte variabile **in mezzo** invece che in fondo: fra «da-assegnare» e
   * «ritocca» c'è l'identificativo di una fotografia.
   *
   * Un prefisso non poteva bastare, e la conseguenza non sarebbe stata un errore
   * ma una risposta sbagliata: «Guida», da lì, avrebbe portato a «Caricare le
   * pagine» — che quella schermata la nomina e non la spiega.
   *
   * Il confronto resta a **pezzi interi**, come prima: `/da-assegnare/abc` non
   * copre `/da-assegnare/abcdef`.
   *
   * *(Il percorso per esteso non si scrive dentro un commento come questo: la
   * stellina seguita dalla barra lo chiuderebbe a metà. È successo scrivendolo.)*
   */
  if (!dichiarato.includes("*")) {
    return percorso === dichiarato || percorso.startsWith(`${dichiarato}/`);
  }

  const pezziDichiarati = dichiarato.split("/");
  const pezzi = percorso.split("/");

  // Più corto del dichiarato: non ci arriva. Più lungo va bene, come il prefisso
  // qui sopra — una schermata figlia resta coperta dalla pagina della madre.
  if (pezzi.length < pezziDichiarati.length) {
    return false;
  }

  return pezziDichiarati.every((pezzo, i) => pezzo === "*" || pezzo === pezzi[i]);
}

/**
 * La pagina di guida che spiega quell'indirizzo — cioè dove porta «Guida».
 *
 * **Vince il percorso più lungo**, e non è un dettaglio: `/il-mio-account` e
 * `/il-mio-account/secondo-fattore` sono due schermate diverse con due pagine
 * diverse, e senza questa regola l'attivazione del secondo fattore
 * rimanderebbe alla pagina dell'account — che non la spiega.
 *
 * `null` quando quell'indirizzo non ha una sua pagina: chi chiama manda
 * all'indice, che è la risposta onesta.
 */
export function guidaPer(percorso: string): VoceGuida | null {
  let migliore: VoceGuida | null = null;
  let lunghezza = -1;

  for (const voce of PAGINE_GUIDA) {
    for (const dichiarato of voce.percorsi) {
      if (copre(dichiarato, percorso) && dichiarato.length > lunghezza) {
        migliore = voce;
        lunghezza = dichiarato.length;
      }
    }
  }

  return migliore;
}

/**
 * Dove deve portare «Guida» da quell'indirizzo: la sua pagina se ce l'ha,
 * altrimenti l'indice.
 */
export function percorsoAiuto(percorso: string): string {
  const voce = guidaPer(percorso);
  return voce === null ? PERCORSO_GUIDA : `${PERCORSO_GUIDA}/${voce.slug}`;
}

/** Il nome del campo che porta alla guida l'indirizzo da cui si è partiti. */
export const CAMPO_RITORNO = "da";

/**
 * **La guida sa da dove sei venuto** — chiesto dal proprietario collaudando la
 * 6f, il 28 settembre 2026: *«la guida non ha il link per tornare indietro»*,
 * cioè alla pagina da cui era partito. «Guida» porta con sé l'indirizzo della
 * schermata in cui si è, e la pagina di guida ci torna con «← Torna dove eri».
 * Dentro la guida non si aggiunge niente: da una pagina di guida si torna con
 * il ritorno che aveva già.
 */
export function conIlRitorno(destinazione: string, percorso: string): string {
  if (percorso === PERCORSO_GUIDA || percorso.startsWith(`${PERCORSO_GUIDA}/`)) {
    return destinazione;
  }
  return `${destinazione}?${CAMPO_RITORNO}=${encodeURIComponent(percorso)}`;
}

/**
 * **Il ritorno si accetta solo se è una pagina di questo sito.** L'indirizzo
 * arriva dalla barra del browser e chiunque può scriverci qualunque cosa: un
 * `//altrove.it` o un `https://…` farebbero del collegamento una porta verso
 * fuori. Si tiene solo un percorso che comincia con una barra sola, senza barre
 * rovesciate e senza caratteri di controllo, e che non sia la guida stessa.
 */
export function ritornoSicuro(da: unknown): string | null {
  if (typeof da !== "string" || da.length === 0 || da.length > 500) {
    return null;
  }
  if (!da.startsWith("/") || da.startsWith("//") || da.includes("\\")) {
    return null;
  }
   
  if (/[\u0000-\u001f\u007f]/.test(da)) {
    return null;
  }
  if (da === PERCORSO_GUIDA || da.startsWith(`${PERCORSO_GUIDA}/`)) {
    return null;
  }
  return da;
}
